import { Link } from 'react-router-dom';
import { Target, Eye, Award, ArrowRight } from 'lucide-react';

const AboutPage = () => {
  const values = [
    {
      icon: Target,
      title: 'Misión',
      description: 'Transformar la educación a través de experiencias de viaje académico que conecten a estudiantes con oportunidades de aprendizaje de clase mundial.',
    },
    {
      icon: Eye,
      title: 'Visión',
      description: 'Ser la agencia líder en turismo académico en Latinoamérica, reconocida por crear experiencias que impactan carreras profesionales.',
    },
    {
      icon: Award,
      title: 'Excelencia',
      description: 'Cada viaje diseñado con los más altos estándares de calidad, seguridad y contenido académico relevante.',
    },
  ];

  return (
    <main data-testid="about-page" className="bg-[#0a0a0a]">
      {/* Hero Section */}
      <section className="relative h-[50vh] min-h-[400px] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=1920&q=80"
            alt="Team collaboration"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1A1A1A]/95 via-[#1A1A1A]/70 to-[#1A1A1A]/50" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
          <span className="inline-block text-[#009DE0] font-semibold text-sm uppercase tracking-wider mb-4">
            Nosotros
          </span>
          <h1 className="text-4xl md:text-6xl font-bold text-white leading-tight">
            Sobre <span className="text-[#009DE0]">Nosotros</span>
          </h1>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-flex items-center gap-2 text-[#009DE0] font-semibold text-sm uppercase tracking-wider mb-4">
                <span className="w-8 h-[2px] bg-[#009DE0]" />
                About Us
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1A1A1A] leading-tight mb-6">
                La Mejor Agencia de
                <br />
                <span className="text-[#009DE0]">Turismo Académico</span>
              </h2>
              <p className="text-[#1A1A1A]/70 text-lg leading-relaxed mb-6">
                Somos una agencia de viajes premium especializada en turismo académico a nivel nacional e internacional. 
                Con más de 5 años de experiencia, hemos transformado la carrera de miles de estudiantes a través de 
                experiencias educativas únicas.
              </p>
              <p className="text-[#1A1A1A]/60 leading-relaxed">
                Más de 2,000 estudiantes viajan con nosotros cada año. Tenemos presencia a nivel nacional e internacional.
                Diseñamos experiencias de primer nivel, llevando a alumnos a congresos, museos, empresas e instituciones 
                relevantes de su carrera.
              </p>
            </div>

            {/* Values Cards */}
            <div className="space-y-4">
              {values.map((value, index) => (
                <div
                  key={index}
                  className="flex gap-5 p-6 bg-[#F8F9FA] rounded-xl hover:shadow-md transition-all border border-transparent hover:border-[#009DE0]/20"
                >
                  <div className="w-14 h-14 bg-[#009DE0] rounded-xl flex items-center justify-center flex-shrink-0">
                    <value.icon size={24} className="text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-[#1A1A1A] mb-2">{value.title}</h3>
                    <p className="text-[#1A1A1A]/60 text-sm leading-relaxed">{value.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Image + Content Section */}
      <section className="py-24 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Images Collage */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <div className="rounded-2xl overflow-hidden h-56">
                  <img
                    src="https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=400&h=350&fit=crop"
                    alt="Team working"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="rounded-2xl overflow-hidden h-44">
                  <img
                    src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=400&h=280&fit=crop"
                    alt="Meeting"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="space-y-4 pt-8">
                <div className="rounded-2xl overflow-hidden h-44">
                  <img
                    src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=400&h=280&fit=crop"
                    alt="Team discussion"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="rounded-2xl overflow-hidden h-56">
                  <img
                    src="https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=400&h=350&fit=crop"
                    alt="Collaboration"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>

            {/* Content */}
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1A1A1A] mb-6">
                Más de 5 años
                <br />
                <span className="text-[#009DE0]">transformando carreras</span>
              </h2>
              <p className="text-[#1A1A1A]/70 leading-relaxed mb-6">
                Desde nuestros inicios, nos hemos dedicado a crear puentes entre estudiantes mexicanos 
                y las mejores oportunidades académicas del mundo. Cada viaje que organizamos está 
                diseñado con un propósito educativo claro.
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  'Más de 2,000 estudiantes viajan con nosotros cada año',
                  'Presencia a nivel nacional e internacional',
                  'Alianzas con las principales universidades del país',
                  'Equipo especializado en turismo académico',
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3 text-[#1A1A1A]/70">
                    <span className="w-2 h-2 bg-[#009DE0] rounded-full" />
                    {item}
                  </li>
                ))}
              </ul>
              <Link
                to="/contacto"
                className="inline-flex items-center gap-2 bg-[#009DE0] text-white px-6 py-3 rounded-full font-semibold hover:bg-[#007BB0] transition-colors"
              >
                Contáctanos
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[#1A1A1A]">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            ¿Quieres ser parte de
            <br />
            <span className="text-[#009DE0]">nuestras alianzas?</span>
          </h2>
          <p className="text-white/60 text-lg mb-10">
            Contáctanos para establecer una alianza estratégica con tu institución o empresa.
          </p>
          <Link
            to="/contacto"
            className="inline-flex items-center gap-2 bg-[#009DE0] text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-[#007BB0] transition-all"
          >
            Contactar Ahora
            <ArrowRight size={20} />
          </Link>
        </div>
      </section>
    </main>
  );
};

export default AboutPage;
